import { reactive } from "vue";
import { toast } from '@/lib/toast';
import { useI18n } from 'vue-i18n'

/**
 * Модель для компонента <v-data-table/>.
 * Пример подключения:
 *  <v-data-table v-bind.sync="tableModel.bind" v-on="tableModel.on">..</v-data-table>
 *
 * .sync у v-bind обязателен, иначе ломается отображение номера просматриваемых записей
 */
export default class RemoteTableModel {
  /**
   * @param options
   * @param {Object[]} options.headers
   * @param {Object} options.options
   * @param {Function} options.loader (query) returns Promise
   */
  constructor(options) {
    const self = this;
    const { t } = useI18n();

    this.quickSearchText = '';
    this.filter = [];
    this.on = {
      'update:options'(options) {
        return self.reload(options);
      },
    };

    (options.headers ?? []).forEach(header => {
      header.title = header.title ? t(header.title) : header.title
    });
    
    this.bind = reactive ({
      headers: options.headers || [],
      options: options.options || {},
      loader: options.loader,
      itemsPerPage: options.options?.itemsPerPage || 10,
      items: [],
      totalEntries: -1,
      loading: false,
    });
  }

  /**
   * @private
   * @return {Promise<void>}
   */
  async loadEntries() {
    const query = {};

    query.search = this.quickSearchText;
    query.filter = this.filter;
    query.range = {
      page: this.bind.options?.page || 1,
      pageSize: this.bind.itemsPerPage,
    };

    const sorts = [];

    if (this.bind.options?.sortBy) {
      this.bind.options.sortBy.forEach((v, k) => {
        sorts.push({
          column: this.bind.options.sortBy[k]?.key,
          order: this.bind.options.sortBy[k]?.order,
        }); 
      });
    }

    query.sort = sorts;
    
    if (this.bind.options?.searchParams) {
      query.searchParams = this.bind.options?.searchParams;
    }
    
    if (this.bind.loader) {
      const isApiRequest = this.bind.options?.isApiRequest;
      try {
        this.bind.loading = true;
        const response = await this.bind.loader(query);
        this.bind.items = isApiRequest ? response.results : response.entries;
        this.bind.totalEntries = isApiRequest ? response.count : response.total;
        this.bind.pageCount = Math.ceil(this.bind.totalEntries / (this.bind.itemsPerPage));
      } catch (e) {
        this.bind.items = [];
        this.bind.totalEntries = -1;
        toast.error(e.message);
      } finally {
        this.bind.loading = false;
      }
    }
  }

  /**
   * @public
   * @param {String} text
   * @return {Promise<void>}
   */
  async quickSearch(text) {
    this.quickSearchText = text;

    return this.loadEntries();
  }

  /**
   * @public
   * @param {Object} [options]
   * @return {Promise<void>}
   */
  async reload(options) {
    if (options?.sortBy && Object.keys(options?.sortBy).length > 0) {
      this.bind.options.sortBy = options.sortBy;
    }

    if (options?.searchParams) {
      this.bind.options.searchParams = options.searchParams;
    }

    return this.loadEntries();
  }

  /**
   * Установить фильтр.
   * @param {Object[]} filter
   */
  setFilter(filter) {
    this.filter = filter;
  }
}
